import { getDashboardStage } from './environmentMode';

export const DASHBOARD_PERF_MARKS = {
    LOGIN_AUTHENTICATED: 'login_authenticated',
    ADMIN_MOUNTED: 'admin_mounted',
    ROLES_READY: 'roles_ready',
    DISPATCH_DATA_READY: 'dispatch_data_ready',
    MERCHANT_DATA_READY: 'merchant_data_ready',
    SESSION_EXPIRED: 'session_expired'
};

const PERF_NAMESPACE = 'gophr-dashboard';
const TELEMETRY_URL = process.env.REACT_APP_DASHBOARD_TELEMETRY_URL || '';
const TELEMETRY_FLUSH_INTERVAL_MS = Number(process.env.REACT_APP_DASHBOARD_TELEMETRY_FLUSH_INTERVAL_MS || 15000);
const TELEMETRY_SAMPLE_RATE = Number(process.env.REACT_APP_DASHBOARD_TELEMETRY_SAMPLE_RATE || 1);
const remoteMetricQueue = [];

let sinkInitialized = false;
let flushTimerId = null;
let telemetrySamplingDecision = null;

const getMarkName = markName => PERF_NAMESPACE + ':' + markName;

const isRemoteTelemetryEnabled = () => Boolean(TELEMETRY_URL);

const shouldSendRemoteTelemetry = () => {
    if (!isRemoteTelemetryEnabled()) {
        return false;
    }

    if (telemetrySamplingDecision === null) {
        telemetrySamplingDecision = TELEMETRY_SAMPLE_RATE >= 1 ? true : Math.random() < TELEMETRY_SAMPLE_RATE;
    }

    return telemetrySamplingDecision;
};

const getMetricStore = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    if (!window.__GOPHR_DASHBOARD_PERF__) {
        window.__GOPHR_DASHBOARD_PERF__ = [];
    }

    return window.__GOPHR_DASHBOARD_PERF__;
};

const buildTelemetryPayload = (metrics, reason) => ({
    app: 'dashboard',
    namespace: PERF_NAMESPACE,
    stage: getDashboardStage(),
    href: typeof window !== 'undefined' && window.location ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    reason: reason || 'interval',
    capturedAt: Date.now(),
    metrics,
});

const transmitTelemetryPayload = payload => {
    if (typeof window === 'undefined' || !isRemoteTelemetryEnabled()) {
        return false;
    }

    const serializedPayload = JSON.stringify(payload);

    if (navigator && typeof navigator.sendBeacon === 'function') {
        try {
            const telemetryBlob = new Blob([serializedPayload], { type: 'application/json' });
            if (navigator.sendBeacon(TELEMETRY_URL, telemetryBlob)) {
                return true;
            }
        } catch (error) {
            return false;
        }
    }

    if (typeof fetch !== 'function') {
        return false;
    }

    fetch(TELEMETRY_URL, {
        method: 'POST',
        headers: {
            'content-type': 'application/json'
        },
        body: serializedPayload,
        keepalive: true,
        credentials: 'omit'
    }).catch(() => null);

    return true;
};

const scheduleTelemetryFlush = () => {
    if (!shouldSendRemoteTelemetry() || flushTimerId !== null) {
        return;
    }

    flushTimerId = setTimeout(() => {
        flushTimerId = null;
        flushDashboardPerformance('interval');
    }, TELEMETRY_FLUSH_INTERVAL_MS);
};

const initializeTelemetrySink = () => {
    if (sinkInitialized || typeof window === 'undefined') {
        return;
    }

    sinkInitialized = true;

    window.addEventListener('pagehide', () => {
        flushDashboardPerformance('pagehide');
    });

    window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushDashboardPerformance('hidden');
        }
    });
};

const appendMetric = metric => {
    const metricStore = getMetricStore();

    initializeTelemetrySink();

    if (metricStore) {
        metricStore.push(metric);
    }

    if (shouldSendRemoteTelemetry()) {
        remoteMetricQueue.push(metric);
        scheduleTelemetryFlush();
    }

    if (typeof console !== 'undefined' && typeof console.info === 'function') {
        if (typeof metric === 'object') {
            console.info('[dashboard-perf]', JSON.stringify(metric));
        } else {
            console.info('[dashboard-perf]', metric);
        }
    }
};

export const flushDashboardPerformance = reason => {
    if (!shouldSendRemoteTelemetry() || remoteMetricQueue.length === 0) {
        return false;
    }

    const metricsToFlush = remoteMetricQueue.splice(0, remoteMetricQueue.length);
    const payload = buildTelemetryPayload(metricsToFlush, reason);
    const transmitResult = transmitTelemetryPayload(payload);

    if (!transmitResult) {
        remoteMetricQueue.unshift(...metricsToFlush);
        scheduleTelemetryFlush();
    }

    return transmitResult;
};

export const markDashboardPerformance = (markName, metadata) => {
    if (typeof window === 'undefined' || !window.performance || typeof window.performance.mark !== 'function') {
        return;
    }

    const resolvedMarkName = getMarkName(markName);
    window.performance.mark(resolvedMarkName);

    appendMetric({
        type: 'mark',
        mark: markName,
        metadata: metadata || null,
        timestamp: Date.now()
    });
};

export const measureDashboardPerformance = (measureName, startMarkName, endMarkName) => {
    if (typeof window === 'undefined' || !window.performance || typeof window.performance.measure !== 'function') {
        return null;
    }

    const resolvedMeasureName = getMarkName(measureName + ':' + Date.now());
    const resolvedStartMark = getMarkName(startMarkName);
    const resolvedEndMark = getMarkName(endMarkName);

    try {
        window.performance.measure(resolvedMeasureName, resolvedStartMark, resolvedEndMark);
        const entries = window.performance.getEntriesByName(resolvedMeasureName);
        const latestEntry = entries && entries.length > 0 ? entries[entries.length - 1] : null;
        const duration = latestEntry ? Math.round(latestEntry.duration) : null;

        appendMetric({
            type: 'measure',
            name: measureName,
            duration,
            startMark: startMarkName,
            endMark: endMarkName,
            timestamp: Date.now()
        });

        return duration;
    } catch (error) {
        return null;
    }
};